/**
 * Compresses Markdown tables into a more compact CSV-like format to save tokens.
 * @param {string} markdownText 
 * @returns {string}
 */
export function compressMarkdownTable(markdownText) {
  if (!markdownText) return markdownText;

  // 按行切分
  const lines = markdownText.split('\n');
  const compressedLines = [];
  let inTable = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // 检测是否是表格行 (包含至少一个管道符 | )
    if (line.startsWith('|') || line.endsWith('|') || line.includes('|')) {
      inTable = true;
      
      // 如果是表格的对齐分割行 (例如 |---|---| 或 | :--- | :---: | )，直接丢弃
      if (/^\|?([\s:-]*\|[\s:-]*)+\|?$/.test(line)) {
         continue;
      }

      // 将 Markdown 表格行转换为紧凑的 CSV 格式
      // 1. 去除首尾的管道符
      let rowContent = line.replace(/^\|/, '').replace(/\|$/, '');
      
      // 2. 按管道符切分单元格，并去除每个单元格首尾的空格
      let cells = rowContent.split('|').map(cell => cell.trim());
      
      // --- 进阶压缩策略 ---
      // 将常见的空值占位符替换为空字符串，减少 Token
      cells = cells.map(cell => {
         const lowerCell = cell.toLowerCase();
         if (lowerCell === 'nan' || lowerCell === 'null' || /^unnamed:\s*\d+$/.test(lowerCell)) {
             return '';
         }
         return cell;
      });

      // 检查该行是否全是空值（或者只有逗号）
      const isEmptyRow = cells.every(cell => cell === '');
      if (isEmptyRow) {
         continue; // 丢弃全是空值的行
      }

      // 3. 使用逗号重新拼接
      compressedLines.push(cells.join(','));
    } else {
      // 如果刚刚离开表格区域，可以加一个空行作为分隔
      if (inTable) {
        inTable = false;
        compressedLines.push('');
      }
      // 非表格行，保留原样 (也可以选择去除多余空格)
      compressedLines.push(line);
    }
  }

  return compressedLines.join('\n');
}

const WEB_URL_CANDIDATE_REGEX = /\b(?:https?:\/\/[^\s<>"'`]+|www\.[^\s<>"'`]+|(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,24}(?:[/?#][^\s<>"'`]*)?)/gi;
const HTTP_PROTOCOL_REGEX = /^https?:\/\//i;
const IPV4_HOST_REGEX = /^(?:\d{1,3}\.){3}\d{1,3}$/;
const VALID_TLD_REGEX = /^(?:[a-z]{2,24}|xn--[a-z0-9-]{2,59})$/i;
const COMMON_BARE_URL_TLDS = new Set([
  'academy', 'aero', 'ai', 'app', 'asia', 'at', 'au', 'be', 'biz', 'blog',
  'br', 'ca', 'cat', 'cc', 'ch', 'cloud', 'club', 'cn', 'co', 'com', 'company',
  'cz', 'de', 'dev', 'dk', 'edu', 'email', 'es', 'eu', 'fi', 'fm', 'fr', 'fun',
  'gg', 'gov', 'gr', 'hk', 'hu', 'id', 'ie', 'il', 'im', 'in', 'info', 'int',
  'io', 'ir', 'it', 'jobs', 'jp', 'kr', 'live', 'ly', 'me', 'mil', 'mobi',
  'museum', 'mx', 'my', 'name', 'net', 'news', 'nl', 'no', 'nz', 'online',
  'org', 'ph', 'pl', 'pro', 'pt', 'ro', 'ru', 'se', 'sg', 'sh', 'shop', 'site',
  'so', 'space', 'store', 'tech', 'tel', 'th', 'to', 'top', 'travel', 'tr', 'tv',
  'tw', 'ua', 'uk', 'us', 'vip', 'vn', 'wiki', 'work', 'world', 'xyz', 'za'
]);

function trimTrailingUrlPunctuation(value) {
  return value.replace(/[)\]\},.;!?，。！？、】【；：]+$/u, '');
}

function isValidIpv4Host(hostname) {
  if (!IPV4_HOST_REGEX.test(hostname)) {
    return false;
  }

  return hostname.split('.').every((segment) => {
    const value = Number(segment);
    return Number.isInteger(value) && value >= 0 && value <= 255;
  });
}

function getRawHostname(candidate) {
  const withoutProtocol = candidate.replace(HTTP_PROTOCOL_REGEX, '');
  const hostWithOptionalPath = withoutProtocol.split(/[/?#]/, 1)[0] || '';
  return hostWithOptionalPath.replace(/:\d+$/, '').trim().toLowerCase();
}

export function normalizeWebUrl(candidate) {
  if (typeof candidate !== 'string') {
    return '';
  }

  const trimmed = trimTrailingUrlPunctuation(candidate.trim());
  if (!trimmed) {
    return '';
  }

  const hasExplicitProtocol = HTTP_PROTOCOL_REGEX.test(trimmed);
  const hasWwwPrefix = /^www\./i.test(trimmed);
  const withProtocol = hasExplicitProtocol
    ? trimmed
    : `https://${trimmed}`;

  const rawHostname = getRawHostname(trimmed);
  if (!rawHostname) {
    return '';
  }

  if (/^[\d.]+$/.test(rawHostname) && !isValidIpv4Host(rawHostname)) {
    return '';
  }

  try {
    const parsedUrl = new URL(withProtocol);
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return '';
    }

    const hostname = parsedUrl.hostname.toLowerCase();
    if (!hostname) {
      return '';
    }

    if (hostname === 'localhost' || isValidIpv4Host(hostname)) {
      return parsedUrl.toString();
    }

    const labels = hostname.split('.');
    if (labels.length < 2) {
      return '';
    }

    const tld = labels[labels.length - 1];
    if (!VALID_TLD_REGEX.test(tld)) {
      return '';
    }

    // 裸文本中的点号表达式更可能是 API/代码（如 chrome.tabs.get）。
    // 没有协议或 www 前缀时，仅将常用真实域名后缀视为网址。
    if (!hasExplicitProtocol && !hasWwwPrefix && !COMMON_BARE_URL_TLDS.has(tld)) {
      return '';
    }

    return parsedUrl.toString();
  } catch (error) {
    return '';
  }
}

export function extractWebUrlsFromText(text) {
  if (typeof text !== 'string' || !text.trim()) {
    return [];
  }

  const urls = [];
  const seen = new Set();

  for (const match of text.matchAll(WEB_URL_CANDIDATE_REGEX)) {
    const rawValue = match[0];
    const startIndex = typeof match.index === 'number' ? match.index : -1;
    if (startIndex > 0 && text[startIndex - 1] === '@') {
      continue;
    }

    const normalizedUrl = normalizeWebUrl(rawValue);
    if (!normalizedUrl || seen.has(normalizedUrl)) {
      continue;
    }

    seen.add(normalizedUrl);
    urls.push(normalizedUrl);
  }

  return urls;
}

/**
 * Replace the body of one fenced code block inside markdown source.
 * `map` is the markdown-it token line range [start, end) of the fence (closing fence included).
 * The opening line's indentation / blockquote prefix is re-applied to every new line, and the
 * fence is lengthened when the new code itself contains a run of the fence character.
 * Returns null when the range does not point at a fence.
 */
export function replaceMarkdownCodeFence(source, map, markup, code) {
  const lines = String(source || '').split('\n');
  const [start, end] = Array.isArray(map) ? map : [];
  if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end <= start || end > lines.length) {
    return null;
  }

  const fenceChar = markup && markup[0] === '~' ? '~' : '`';
  const lineEnding = lines[start].endsWith('\r') ? '\r' : '';
  const openingLine = lines[start].replace(/\r$/, '');
  const markerIndex = openingLine.indexOf(fenceChar.repeat(3));
  if (markerIndex < 0) return null;

  let originalLength = 0;
  while (openingLine[markerIndex + originalLength] === fenceChar) originalLength += 1;
  const info = openingLine.slice(markerIndex + originalLength);

  // "1. ```js" or "- ```js": the list marker belongs to the opening line only;
  // following lines continue the item with the same width of spaces.
  const openingPrefix = openingLine.slice(0, markerIndex);
  const continuationPrefix = openingPrefix.replace(/(\d+[.)]|[-*+])(?=\s|$)/g, marker => ' '.repeat(marker.length));

  const body = String(code || '').replace(/\r\n/g, '\n').replace(/\n$/, '');
  let longestRun = 0;
  let run = 0;
  for (const char of body) {
    run = char === fenceChar ? run + 1 : 0;
    if (run > longestRun) longestRun = run;
  }
  const fence = fenceChar.repeat(Math.max(originalLength, longestRun + 1));

  const replacement = [
    `${openingPrefix}${fence}${info}${lineEnding}`,
    ...(body ? body.split('\n').map(line => `${continuationPrefix}${line}${lineEnding}`) : []),
    `${continuationPrefix}${fence}${lineEnding}`
  ];

  // An unterminated fence (e.g. truncated output) runs to the end of its block; it is closed explicitly here.
  return [...lines.slice(0, start), ...replacement, ...lines.slice(end)].join('\n');
}
